import { createPortal } from 'react-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';
import { Trans } from 'react-i18next';
import { BsQuestionCircle } from 'react-icons/bs';

import ActAsSessionOverlay from './ActAsSessionOverlay';
import BayTooltip from '../../BayTooltip';
import stopActingAsRequest from './stopActingAs';
import logOffRequest from './logOff';
import type { TranslationFunction } from '../../types/common';
import type { BooleanCallback } from '../../types/callbacks';

const BannerDiv = styled.div<{ $bg?: string; $border?: string }>`
	background-color: ${({ $bg }) => $bg};
	width: 100% !important;
	position: fixed !important;
	z-index: 999 !important;
	padding: 0.5rem;
	min-height: 45px;
	height: auto;
	top: 0 !important;
	left: 0 !important;
	border: ${({ $border }) => $border};

	@media (max-width: 575px) {
		padding: 7px 14px;
		min-height: auto;
	}
`;

const BannerInner = styled.div<{ $color?: string; $newStyle?: boolean; $purpleSmall?: boolean }>`
	/* wrap, not nowrap: the refusal message is a third child on this row, and on a
	   nowrap line it squeezed the buttons into breaking mid-label at exactly the
	   moment the user needs to read it and click again. Same treatment the twin
	   control in the account menu gets in myaccount.scss.

	   This is not a no-op for the usual two children: where the sentence and the
	   buttons used to overflow and shrink to min-content -- a long acted-as name,
	   or the purple " from InternalBAY." suffix -- the buttons now drop to their
	   own line instead. That is the better failure mode, and the ResizeObserver
	   below republishes --actas-banner-height so the nav re-anchors to the taller
	   banner rather than overlapping it. */
	flex-wrap: wrap;
	color: ${({ $color }) => $color};

	.actasBanner-error {
		flex-basis: 100%;
	}
	${({ $newStyle }) =>
		$newStyle
			? `
		font-family: Inter, sans-serif;
		font-size: 0.875rem;
		line-height: 1.5;
	`
			: ''}

	@media (max-width: 575px) {
		flex-wrap: wrap;
		width: 100%;
		${({ $purpleSmall }) =>
			$purpleSmall
				? `
			font-size: 0.75rem;
			line-height: 1.5;
			font-family: Inter, sans-serif;
			font-weight: 400;
		`
				: ''}
	}
`;

const BannerButtonsWrapper = styled.div`
	@media (max-width: 575px) {
		width: 100%;
	}
`;

/* Self-contained styled button (no Bootstrap `.btn` class) so its box model is
   identical to BannerButton — same height/line-height/display — and the two buttons
   center on the same baseline purely via the parent flex row, with no pixel offsets. */
const ChangeUserButton = styled.button`
	box-sizing: border-box;
	overflow: visible;
	margin: 0;
	font-family: inherit;
	text-transform: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 32px;
	font-size: 0.875rem;
	line-height: 1;
	text-align: center;
	text-decoration: none;
	user-select: none;
	cursor: pointer;

	@media (max-width: 575px) {
		flex: 1;
	}
`;

const BannerButton = styled.button<{ $newStyle?: boolean }>`
	box-sizing: border-box;
	overflow: visible;
	margin: 0;
	font-family: inherit;
	text-transform: none;
	display: ${({ $newStyle }) => ($newStyle ? 'inline-flex' : 'inline-block')};
	align-items: center;
	justify-content: center;
	height: ${({ $newStyle }) => ($newStyle ? '32px' : 'auto')};
	font-weight: 400;
	text-align: center;
	text-decoration: none;
	vertical-align: middle;
	user-select: none;
	border: 1px solid transparent;
	font-size: ${({ $newStyle }) => ($newStyle ? '0.875rem' : '1rem')};
	transition:
		color 0.15s ease-in-out,
		background-color 0.15s ease-in-out,
		border-color 0.15s ease-in-out,
		box-shadow 0.15s ease-in-out;
	border-color: ${({ $newStyle }) => ($newStyle ? 'var(--bs-danger)' : 'var(--bs-white)')};
	color: ${({ $newStyle }) => ($newStyle ? 'var(--bs-white)' : 'var(--bs-blue-500)')};
	background-color: ${({ $newStyle }) => ($newStyle ? 'var(--bs-danger)' : 'var(--bs-white)')};
	line-height: ${({ $newStyle }) => ($newStyle ? '1' : '1.3')};
	cursor: pointer;
	z-index: auto !important;

	&:hover,
	&:focus {
		color: ${({ $newStyle }) => ($newStyle ? 'var(--bs-white)' : 'var(--bs-blue-500)')};
		text-decoration: none;
		background-color: ${({ $newStyle }) =>
			$newStyle ? 'var(--bs-danger-emphasis, #b02a37)' : 'var(--bs-gray-50)'};
	}

	@media (max-width: 575px) {
		flex: 1;
	}
`;

interface ActAsBannerProps {
	userFirstName: string;
	userLastName: string;
	userPublicId: string;
	actorAccount?: boolean | string;
	actingAsSource?: string;
	platform?: string;
	openActAsModal?: BooleanCallback;
	t?: TranslationFunction;
}

const ActAsBanner = ({
	userFirstName,
	userLastName,
	// unused now that setActingAs('') resolves "return to self" server-side --
	// kept in the prop contract since callers still pass it.
	userPublicId: _userPublicId,
	actorAccount,
	actingAsSource,
	openActAsModal,
	t,
	platform,
}: ActAsBannerProps) => {
	const bannerRef = useRef<HTMLDivElement>(null);
	const [stopping, setStopping] = useState(false);
	const [stopError, setStopError] = useState<string | null>(null);

	// Enforce positioning via inline style with !important so no external CSS
	// rule (including ones with !important) can override it after content loads.
	useEffect(() => {
		const el = bannerRef.current;
		if (!el) return;
		el.style.setProperty('position', 'fixed', 'important');
		// Owns the top edge; the User Tour banner sits under it (useUserTourBannerOffset).
		el.style.setProperty('top', '0', 'important');
		el.style.setProperty('left', '0', 'important');
		el.style.setProperty('z-index', '999', 'important');
		el.style.setProperty('width', '100%', 'important');
	}, []);
	const isClientPlatform = platform === 'client';
	const isSocialPlatform = platform === 'social';
	const useNewStyle = isClientPlatform || isSocialPlatform;
	// Social without actorAccount = initial launch from InternalBAY → purple
	// Social with actorAccount = changed user within social → yellow
	const usePurpleStyle = isSocialPlatform && !actorAccount;
	const useYellowStyle = isClientPlatform || (isSocialPlatform && !!actorAccount);

	// All banner variants (purple, yellow-social, yellow-client) have a height
	// that can vary slightly from the fixed offsets in navStyles: the acting-as
	// name can wrap to a second line, the web font swaps in after first paint,
	// and the viewport can rotate/zoom. A fixed nav offset can't track that, so
	// publish the banner's live measured height as --actas-banner-height and let
	// the fixed top-nav anchor to it (see navStyles) instead of trusting the
	// hardcoded value to exactly match.
	useLayoutEffect(() => {
		const el = bannerRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return undefined;

		const publishHeight = () => {
			// getBoundingClientRect().height is sub-pixel exact; offsetHeight rounds
			// to an integer, which can overshoot the real (fractional) banner height
			// and leave a hairline gap that scrolling content shows through.
			document.body.style.setProperty(
				'--actas-banner-height',
				`${el.getBoundingClientRect().height}px`,
			);
		};

		// Measure synchronously before paint so the nav never flashes a gap,
		// then keep it in sync as the banner reflows (wrap, font swap, resize).
		publishHeight();
		const observer = new ResizeObserver(publishHeight);
		observer.observe(el);

		return () => {
			observer.disconnect();
			document.body.style.removeProperty('--actas-banner-height');
		};
	}, []);

	const stopActingAs = async () => {
		// Shown on click, not after the round trip: setActingAs plus the reload
		// take seconds, and the button gave no feedback at all in between.
		setStopping(true);
		setStopError(null);

		const { ok, message } = await stopActingAsRequest();

		// A refused stop leaves the user acting as someone else, so it has to say
		// so: without this the overlay just came back down and the only signal was
		// the page not reloading.
		if (!ok) {
			setStopping(false);
			setStopError(message || t('stopActingAsFailed'));
		}
	};

	const bannerBg = usePurpleStyle
		? 'var(--bs-purple-100, #E0CFFC)'
		: useYellowStyle
			? 'var(--bs-yellow-100)'
			: '#1571f0';

	const bannerBorder = usePurpleStyle
		? '1px solid var(--bs-purple-200, #C29FFA)'
		: useYellowStyle
			? '1px solid var(--bs-yellow-200)'
			: 'none';

	const bannerColor = usePurpleStyle
		? '#520DC2'
		: useYellowStyle
			? '#332701'
			: '#fff';

	const handleButtonClick = () => {
		if (platform === 'social') {
			logOffRequest('social', { parentStopActing: Boolean(actorAccount) });
		} else {
			stopActingAs();
		}
	};

	if (typeof document === 'undefined') {
		return null;
	}

	return createPortal(
		<>
			<ActAsSessionOverlay
				show={stopping}
				label={t('stoppingSecureActAsSession')}
				name={`${userFirstName} ${userLastName}`.trim()}
				platform={platform}
				t={t}
			/>
			<BannerDiv
				ref={bannerRef}
				$bg={bannerBg}
				$border={bannerBorder}
				className={`d-flex justify-content-center align-items-center actas-banner-wrapper ${
					platform === 'social' && actingAsSource === 'internal' ? 'internal' : ''
				}`}
			>
				<BannerInner
					$color={bannerColor}
					$purpleSmall={usePurpleStyle}
					$newStyle={useNewStyle}
					className="d-flex justify-content-center align-items-center gap-3 actasBanner"
				>
					<div className="d-flex align-items-center justify-content-center">
						{/* Whole sentence in one inline span: the parent div is flex, and
					    flexbox strips whitespace between flex items, so separate spans
					    would collapse the spaces around the name. Keeping it inline lets
					    normal text spacing render correctly. */}
						<span>
							{t('youAreActingAs')}{' '}
							<span className="user-name">
								{`${userFirstName} ${userLastName}`.trim()}
							</span>
							{usePurpleStyle ? ' from InternalBAY.' : '.'}
						</span>
						{useNewStyle && (
							<BayTooltip
								overlay={
									<Trans
										i18nKey={
											usePurpleStyle
												? 'actAsAnotherUserSessionFromInternalBayTooltipText'
												: 'actAsAnotherUserSessionTooltipText'
										}
										components={{ div: <div />, i: <i /> }}
									/>
								}
								placement="bottom"
								overlayMaxWidth={350}
							>
								<span className="cursor-pointer d-flex align-items-center ms-2">
									<BsQuestionCircle color="var(--bs-gray-700)" size={14} />
								</span>
							</BayTooltip>
						)}
					</div>

					<BannerButtonsWrapper className="d-flex align-items-center gap-3 justify-content-center justify-content-sm-start">
						{useNewStyle && (
							<ChangeUserButton
								className="changeUserButton bg-white border border-1 border-400 text-700 px-3 py-2 fw-bold rounded-1"
								onClick={() => openActAsModal(true)}
							>
								{t('changeUser')}
							</ChangeUserButton>
						)}
						<BannerButton
							$newStyle={useNewStyle}
							className={`actasBanner-button border border-1 rounded-1 px-3 py-2 ${
								useNewStyle ? 'border-danger fw-bold' : 'border-white'
							}`}
							onClick={handleButtonClick}
						>
							{t('stopActingAs')}
						</BannerButton>
					</BannerButtonsWrapper>

					{/* role="alert" so the refusal is announced: the button that failed
					    is several elements away, and nothing else on the banner changes
					    when a stop is refused. */}
					{stopError && (
						<span
							role="alert"
							className="actasBanner-error text-danger bg-white rounded-1 px-2 py-1"
						>
							{stopError}
						</span>
					)}
				</BannerInner>
			</BannerDiv>
		</>,
		document.body,
	);
};

export default ActAsBanner;
